import React, { useRef } from 'react';

import { useInView } from 'react-intersection-observer';

import { calculatePosition } from './shared/calculatePosition';
import { calculateRootMargin } from './shared/calculateRootMargin';
import { handleCallbacks } from './shared/handleCallbacks';
import type { PositionChangeData, WaypointPosition } from './shared/types';

// Re-export types for consumers
export type { PositionChangeData, WaypointPosition };

type Props = Readonly<{
	onEnter?: () => void;
	onLeave?: () => void;
	onPositionChange?: (data: PositionChangeData) => void;
	topOffset?: number | string;
	bottomOffset?: number | string;
	fireOnRapidScroll?: boolean;
	horizontal?: boolean;
}>;

/**
 * A component that triggers callbacks when it enters or leaves the viewport.
 * This is a wrapper around react-intersection-observer that provides a similar API to react-waypoint.
 *
 * Renders a zero-size `<span>` that still participates in normal document flow. As a direct
 * child of a flex container with `gap`, it silently consumes a full gap unit (or a line box in
 * block flow), e.g. indenting or spacing out the sibling after it. Take it out of flow (e.g.
 * `absolute`) or place it outside the gapped container if this matters visually.
 *
 * @param onEnter - Callback fired when the waypoint enters the viewport
 * @param onLeave - Callback fired when the waypoint leaves the viewport
 * @param onPositionChange - Callback fired when the waypoint's position changes relative to the viewport
 * @param topOffset - Offset from the top of the viewport (in pixels or percentage). When horizontal is true, this acts as leftOffset.
 * @param bottomOffset - Offset from the bottom of the viewport (in pixels or percentage). When horizontal is true, this acts as rightOffset.
 * @param fireOnRapidScroll - Whether to fire callbacks during rapid scrolling (default: true)
 * @param horizontal - Whether to track horizontal scroll instead of vertical (default: false)
 */
export const Waypoint: React.FC<Props> = ({
	onEnter,
	onLeave,
	onPositionChange,
	topOffset = 0,
	bottomOffset = 0,
	fireOnRapidScroll = true,
	horizontal = false,
}) => {
	const previousPositionRef = useRef<WaypointPosition | undefined>(undefined);

	const { ref } = useInView({
		onChange: (inView, entry) => {
			const currentPosition = calculatePosition(entry, horizontal);
			const previousPosition = previousPositionRef.current;

			// Handle callbacks
			handleCallbacks({
				inView,
				entry,
				currentPosition,
				previousPosition,
				onEnter,
				onLeave,
				onPositionChange,
				horizontal,
			});

			previousPositionRef.current = currentPosition;
		},
		rootMargin: calculateRootMargin(topOffset, bottomOffset, horizontal),
		// Only skip observers if fireOnRapidScroll is false
		skip: !fireOnRapidScroll && !onEnter && !onLeave && !onPositionChange,
	});

	// Using a span to match react-waypoint behavior - it participates in document flow
	// and scrolls with its container
	return <span ref={ref} style={{ fontSize: 0, lineHeight: 0 }} />;
};
